/**
 * /hotels/:code/book — Hotel booking form + material disclosures.
 *
 * On mount:
 *   1. Reads rateKey + rateType from the URL.
 *   2. If rateType === "RECHECK", calls POST /api/hotels/checkrate
 *      and uses the refreshed rate. Shows a "Price updated" notice
 *      if the returned price differs from the URL param `expected`.
 *   3. Fetches hotel detail for name/address context.
 *
 * Idempotency:
 *   - Generates a UUID once on mount, regenerated on any form change.
 *   - Sent as `Idempotency-Key` header — backend replays within 5 min.
 *   - Confirm button disabled while submitting or if any pax field
 *     is blank.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Info, Sparkles, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

function uuidHex() {
  return "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
}

export default function HotelBookingPage() {
  const { code } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialRateKey = sp.get("rateKey");
  const initialRateType = sp.get("rateType") || "BOOKABLE";
  const checkIn = sp.get("checkIn");
  const checkOut = sp.get("checkOut");

  const [rateKey, setRateKey] = useState(initialRateKey);
  const [rate, setRate] = useState(null);
  const [priceChanged, setPriceChanged] = useState(false);
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(uuidHex);

  const [holder, setHolder] = useState({ name: "", surname: "", email: "", phone: "" });
  const [adultPaxes, setAdultPaxes] = useState([
    { roomId: 1, type: "AD", name: "", surname: "" },
    { roomId: 1, type: "AD", name: "", surname: "" },
  ]);
  const [childPaxes, setChildPaxes] = useState([]);

  // Regenerate idempotency key whenever the caller edits the form —
  // guarantees a legitimately-modified booking gets a fresh HB call.
  const bumpKey = useCallback(() => setIdempotencyKey(uuidHex()), []);

  // Load hotel context + always CheckRate to surface material
  // disclosures (cancellation, comments, promotions, taxes) before
  // the user confirms. Hotelbeds cert Step 3 requires these to
  // render verbatim on the pre-confirm page even for BOOKABLE rates
  // — CheckRate is a safe idempotent re-quote for either rateType.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const detailReq = api.get(`/hotels/detail/${code}`);
    const rateReq = api.post("/hotels/checkrate", { rateKey: initialRateKey });
    Promise.all([detailReq, rateReq]).then(([dResp, rResp]) => {
      if (!alive) return;
      setHotel(dResp.data);
      const refreshed = rResp.data;
      setRate(refreshed);
      if (refreshed.rateKey) setRateKey(refreshed.rateKey);
      if (
        initialRateType === "RECHECK"
        && refreshed.sellingRate
        && sp.get("expected")
        && Number(sp.get("expected")) !== Number(refreshed.sellingRate)
      ) {
        setPriceChanged(true);
      }
    }).catch((e) => {
      if (!alive) return;
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, initialRateKey, initialRateType, sp]);

  const setHolderField = (k, v) => { setHolder((h) => ({ ...h, [k]: v })); bumpKey(); };
  const setAdultField = (i, k, v) => {
    setAdultPaxes((prev) => prev.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
    bumpKey();
  };
  const setChildField = (i, k, v) => {
    setChildPaxes((prev) => prev.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
    bumpKey();
  };

  const canSubmit = useMemo(() => {
    if (!rateKey) return false;
    if (!holder.name.trim() || !holder.surname.trim() || !holder.email.trim()) return false;
    if (adultPaxes.some((p) => !p.name.trim() || !p.surname.trim())) return false;
    if (childPaxes.some((p) => !p.name.trim() || !p.surname.trim() || !p.age)) return false;
    return true;
  }, [rateKey, holder, adultPaxes, childPaxes]);

  const cancellation = rate?.cancellationPolicies?.[0];

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      const { data } = await api.post(
        "/hotels/book",
        {
          rateKey,
          holder,
          rooms: [{ paxes: [...adultPaxes, ...childPaxes] }],
        },
        { headers: { "Idempotency-Key": idempotencyKey }, timeout: 90000 },
      );
      nav(`/hotels/booking/${encodeURIComponent(data.hotelbeds_reference)}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="hotel-booking-page" className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-6 pb-24">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/20 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        <div className="mt-6 flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-tb-navy text-3xl md:text-4xl leading-tight font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
            {loading ? "Loading…" : `Book ${hotel?.name || "hotel"}`}
          </h1>
          <PoweredByHotelbeds />
        </div>
        <p className="mt-1 font-hand text-tb-navy/60 text-sm italic">
          {checkIn} → {checkOut}
        </p>

        {priceChanged && (
          <div data-testid="hotel-booking-price-updated" className="mt-4 rounded-xl border-2 border-tb-gold bg-tb-gold/10 px-4 py-3 flex items-start gap-2 font-hand text-tb-navy text-sm">
            <Sparkles className="w-4 h-4 mt-0.5 text-tb-gold shrink-0" />
            <span>Price refreshed from the supplier. The current total is what will be charged.</span>
          </div>
        )}

        {/* Guest form */}
        <section className="mt-6 rounded-2xl border-2 border-tb-navy/15 bg-white p-5 md:p-6 space-y-5">
          <div>
            <h2 className="text-tb-navy text-xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
              Lead guest
            </h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField testId="hotel-booking-holder-name"    label="First name"    value={holder.name}    onChange={(v) => setHolderField("name", v)} />
              <TextField testId="hotel-booking-holder-surname" label="Last name"     value={holder.surname} onChange={(v) => setHolderField("surname", v)} />
              <TextField testId="hotel-booking-holder-email"   label="Email"         value={holder.email}   onChange={(v) => setHolderField("email", v)} type="email" />
              <TextField testId="hotel-booking-holder-phone"   label="Phone"         value={holder.phone}   onChange={(v) => setHolderField("phone", v)} />
            </div>
          </div>

          <div>
            <h2 className="text-tb-navy text-xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
              Adult guests
            </h2>
            <div className="mt-3 space-y-3">
              {adultPaxes.map((p, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <TextField testId={`hotel-booking-adult-${i}-name`}    label={`Adult ${i + 1} first name`} value={p.name}    onChange={(v) => setAdultField(i, "name", v)} />
                  <TextField testId={`hotel-booking-adult-${i}-surname`} label={`Adult ${i + 1} last name`}  value={p.surname} onChange={(v) => setAdultField(i, "surname", v)} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Material disclosures block */}
        <section data-testid="hotel-booking-disclosures" className="mt-6 rounded-2xl border-2 border-tb-navy/25 bg-white p-5 md:p-6 space-y-3">
          <div className="flex items-center gap-2 font-hand text-tb-navy text-sm uppercase tracking-widest">
            <ShieldCheck className="w-4 h-4" /> Before you confirm
          </div>
          {cancellation?.from && (
            <div className="font-hand text-tb-navy text-base">
              <b>Cancellation:</b> Free cancellation until{" "}
              <span data-testid="hotel-booking-cancel-deadline">
                {new Date(cancellation.from).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
              </span>
              , local hotel time. After that a fee of <b>{cancellation.amount} {rate?.raw?.hotel?.currency || ""}</b> applies.
            </div>
          )}
          {rate?.rateComments && (
            <div className="font-hand text-tb-navy/85 text-sm italic border-t border-tb-navy/15 pt-3">
              {rate.rateComments}
            </div>
          )}
          {rate?.promotions?.length > 0 && (
            <ul className="space-y-1 font-hand text-emerald-700 text-sm">
              {rate.promotions.map((p, i) => (
                <li key={i} className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {p.name}</li>
              ))}
            </ul>
          )}
          {rate?.rateClass === "NRF" && (
            <div className="flex items-start gap-2 font-hand text-red-700 text-sm border-t border-tb-navy/15 pt-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Non-refundable rate. If you cancel or don&apos;t show up, the full amount is charged.</span>
            </div>
          )}
          <div className="pt-2">
            <PoweredByHotelbeds size="xs" />
          </div>
        </section>

        {err && (
          <div data-testid="hotel-booking-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-hand text-red-700 text-sm">
            {err}
          </div>
        )}

        <button
          type="button"
          data-testid="hotel-booking-confirm"
          disabled={!canSubmit || submitting}
          onClick={submit}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-45 hover:brightness-110"
        >
          {submitting ? "~ confirming ~" : "Confirm booking"}
        </button>
        <p className="mt-3 text-center font-hand text-tb-navy/45 text-xs italic flex items-center justify-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          By tapping confirm you agree to the supplier&apos;s cancellation and rate terms above.
        </p>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testId, type = "text" }) {
  return (
    <label className="flex flex-col">
      <span className="font-hand text-tb-navy/70 text-[11px] uppercase tracking-widest">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="mt-1 border-b-2 border-tb-navy/20 focus:border-tb-navy py-2 bg-transparent font-hand text-tb-navy text-base outline-none"
      />
    </label>
  );
}
